import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import useGlContext, { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DmsBadge, DocHead, Panel, Section, Totals, run } from "../sales/dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";
import { PartsStock, BackOrders } from "./PartsStock";

// Parts Sales: parts quotations and orders for trade and retail customers. Booking an order
// reserves stock in its warehouse (what is short goes on back order and is reserved when stock
// arrives); reserved parts are issued, issues are invoiced, invoiced parts can be returned.

function useLookup(type) {
  const { data } = useLoad(() => dmsService.listLookups({ lookup_type: type, status: "ACTIVE" }), [type]);
  return (data || []).map((r) => ({ label: r.name, value: r.code }));
}

const blankLine = () => ({ item_id: null, quantity: 1, unit_price: "", discount_amount: "" });

// Part lines with free stock in the chosen warehouse, priced live (price list, supersession, VAT).
function PartLines({ lines, setLines, warehouseId, priceListId, currency }) {
  const parts = useLoad(() => dmsService.searchParts({ warehouse_id: warehouseId || undefined, size: 100 }), [warehouseId]);
  const [preview, setPreview] = useState(null);
  const options = (parts.data || []).map((p) => ({
    label: `${p.sku} – ${p.name}${p.available !== undefined ? ` · ${Number(p.available)} free` : ""}${p.superseded ? " · superseded" : ""}`,
    value: p.id,
  }));
  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const payload = lines.filter((l) => l.item_id).map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity || 1), unit_price: l.unit_price === "" ? undefined : Number(l.unit_price), discount_amount: Number(l.discount_amount || 0) }));
  const key = JSON.stringify([payload, priceListId, currency]);
  useEffect(() => {
    if (!payload.length || !currency) {
      setPreview(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const res = await dmsService.pricePreview({ category: "PARTS", price_list_id: priceListId || null, currency, lines: payload });
        setPreview(res && res.data ? res.data : null);
      } catch {
        setPreview(null);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (
    <>
      {lines.map((l, i) => {
        // The preview has a line for every row with a part chosen, in order.
        const at = l.item_id ? lines.slice(0, i + 1).filter((x) => x.item_id).length - 1 : -1;
        const p = preview && at >= 0 ? preview.lines[at] || null : null;
        return (
          <div key={i} className="mb-2">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <div style={{ flex: 1, minWidth: 280 }}>
                <AppDropdown id={`pl-it-${i}`} value={l.item_id} filter options={options} onChange={(e) => set(i, { item_id: e.value })} placeholder="Part (SKU, name or OEM number)" />
              </div>
              <input type="number" min={0.001} step="any" className="form-control" style={{ ...inputStyle, width: 90 }} aria-label="Quantity" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
              <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 120 }} aria-label="Unit price" placeholder={p ? fmtMoney(p.list_price) : "Price"} value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} />
              <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 110 }} aria-label="Discount" placeholder="Discount" value={l.discount_amount} onChange={(e) => set(i, { discount_amount: e.target.value })} />
              <div style={{ width: 110, textAlign: "right", fontWeight: 600 }}>{p ? fmtMoney(p.line_total) : ""}</div>
              {lines.length > 1 ? (
                <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                  Remove
                </button>
              ) : null}
            </div>
            {p && p.replaced_sku ? <div style={hint}>Superseded: {p.replaced_sku} is replaced by {p.sku}</div> : null}
          </div>
        );
      })}
      <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => setLines([...lines, blankLine()])}>
        Add part
      </button>
      {preview ? <Totals goods="Parts" doc={{ ...preview.totals, currency }} /> : null}
    </>
  );
}

function NewDoc({ kind, onHide, onDone }) {
  const fin = useFinOptions(["locations"]);
  const gl = useGlContext();
  const team = useDmsOptions(["salespersons", "price-lists"]);
  const whs = useLoad(() => dmsService.listWarehouses({ status: "ACTIVE" }), []);
  const customers = useLoad(() => finService.listCustomers({ size: 200 }), []);
  const [h, setH] = useState({ location_id: null, warehouse_id: null, customer_account_id: null, price_list_id: null, salesperson_employee_id: null, customer_po: "", remarks: "" });
  const [lines, setLines] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const loc = h.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const whOptions = (whs.data || []).map((w) => ({ label: `${w.code} – ${w.name}${w.is_default ? " (default)" : ""}`, value: w.id }));
  const wh = h.warehouse_id || (whOptions[0] ? whOptions[0].value : null);
  const pl = (team["price-lists"] || []).find((o) => o.value === h.price_list_id);
  const currency = pl ? pl.row.currency : (gl.ledger && gl.ledger.currency) || "AED";
  const save = async () => {
    setBusy(true);
    const body = {
      location_id: loc,
      warehouse_id: wh,
      customer_account_id: h.customer_account_id,
      price_list_id: h.price_list_id || undefined,
      salesperson_employee_id: h.salesperson_employee_id || undefined,
      remarks: h.remarks || undefined,
      lines: lines.filter((l) => l.item_id).map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity || 1), unit_price: l.unit_price === "" ? undefined : Number(l.unit_price), discount_amount: Number(l.discount_amount || 0) })),
    };
    const res = await run(() => (kind === "quote" ? dmsService.saveQuote(null, { ...body, quote_type: "PARTS" }) : dmsService.saveOrder(null, { ...body, order_category: "PARTS", customer_po: h.customer_po || undefined })));
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <Panel
      title={kind === "quote" ? "New parts quotation" : "New parts order"}
      onHide={() => !busy && onHide()}
      width={1000}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "0 12px" }}>
        <Field label="Customer" required>
          <AppDropdown id="pd-cust" value={h.customer_account_id} filter options={(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id }))} onChange={(e) => setH({ ...h, customer_account_id: e.value })} />
        </Field>
        <Field label="Location">
          <AppDropdown id="pd-loc" value={loc} options={fin.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} />
        </Field>
        <Field label="Warehouse">
          <AppDropdown id="pd-wh" value={wh} options={whOptions} onChange={(e) => setH({ ...h, warehouse_id: e.value })} />
        </Field>
        <Field label="Price list">
          <AppDropdown id="pd-pl" value={h.price_list_id} options={[{ label: "Item selling price", value: null }, ...(team["price-lists"] || [])]} onChange={(e) => setH({ ...h, price_list_id: e.value })} />
        </Field>
        <Field label="Parts advisor">
          <AppDropdown id="pd-sp" value={h.salesperson_employee_id} options={(team.salespersons || []).map((s) => ({ label: s.row.employee_name, value: s.row.employee_id }))} onChange={(e) => setH({ ...h, salesperson_employee_id: e.value })} placeholder="Me" />
        </Field>
        {kind === "order" ? (
          <Field label="Customer PO">
            <input className="form-control" style={inputStyle} value={h.customer_po} onChange={(e) => setH({ ...h, customer_po: e.target.value })} />
          </Field>
        ) : null}
      </div>
      <PartLines lines={lines} setLines={setLines} warehouseId={wh} priceListId={h.price_list_id} currency={currency} />
      <Field label="Remarks">
        <input className="form-control" style={inputStyle} value={h.remarks} onChange={(e) => setH({ ...h, remarks: e.target.value })} />
      </Field>
    </Panel>
  );
}

function QuoteView({ id, onHide, onChanged, onOrder }) {
  const { data: q, loading, error, reload } = useLoad(() => dmsService.getQuote(id), [id]);
  const lost = useLookup("LOST_REASON");
  const [losing, setLosing] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  const doIt = (a, data) => run(() => dmsService.quoteAction(id, a, data), refresh);
  return (
    <Panel title={q ? `Parts quotation ${q.quote_number}${q.revision ? ` rev ${q.revision}` : ""}` : "Parts quotation"} onHide={onHide}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {q ? (
          <>
            <DocHead title="" number={q.customer_name} status={q.status} sub={`${q.location_name || ""} · ${formatDate(q.quote_date)} · valid until ${formatDate(q.valid_until)}`}>
              {q.status === "DRAFT" ? <PrimaryButton onClick={() => doIt("submit")}>Submit</PrimaryButton> : null}
              {["APPROVED", "PENDING_WITH_CUSTOMER"].includes(q.status) ? (
                <PrimaryButton
                  onClick={() =>
                    run(
                      () => dmsService.quoteAction(id, "accept"),
                      (res) => {
                        refresh();
                        onOrder(res.data.so_id);
                      }
                    )
                  }
                >
                  Accept → order
                </PrimaryButton>
              ) : null}
              {q.status === "APPROVED" ? <SecondaryButton onClick={() => doIt("send")}>Mark sent</SecondaryButton> : null}
              {["APPROVED", "PENDING_WITH_CUSTOMER", "EXPIRED"].includes(q.status) ? <SecondaryButton onClick={() => setLosing(true)}>Lost</SecondaryButton> : null}
              {["DRAFT", "APPROVED", "PENDING_WITH_CUSTOMER"].includes(q.status) ? <SecondaryButton onClick={() => window.confirm("Cancel this quotation?") && doIt("cancel")}>Cancel</SecondaryButton> : null}
              <SecondaryButton onClick={() => dmsService.quotePdf(id, `${q.quote_number}.pdf`)}>PDF</SecondaryButton>
            </DocHead>
            <Section title="Parts">
              <DataTable
                rows={q.lines}
                rowKey={(l) => l.line_no}
                columns={[
                  { label: "#", render: (l) => l.line_no },
                  { label: "Part", render: (l) => l.description },
                  { label: "Qty", num: true, render: (l) => Number(l.quantity) },
                  { label: "Price", num: true, render: (l) => fmtMoney(l.unit_price) },
                  { label: "Net", num: true, render: (l) => fmtMoney(l.net_amount) },
                  { label: "VAT", num: true, render: (l) => fmtMoney(l.tax_amount) },
                ]}
              />
              <Totals goods="Parts" doc={q} />
            </Section>
            <Section title="Action history">
              <ActionHistory actions={q.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={losing}
        title="Mark as lost"
        onHide={() => setLosing(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.quoteAction(id, "lost", clean)}
        fields={[
          { key: "lost_reason", label: "Reason", type: "select", required: true, options: lost },
          { key: "remarks", label: "Remarks", max: 1000 },
        ]}
      />
    </Panel>
  );
}

function ReturnDrawer({ issue, onHide, onDone }) {
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const lines = issue.lines.map((l, i) => ({ issue_line_id: l.id, quantity: Number(qty[i] || 0) })).filter((l) => l.quantity > 0);
    const res = await run(() => dmsService.returnParts(issue.id, { lines, reason }));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <Panel title={`Return against ${issue.issue_number}`} onHide={() => !busy && onHide()} width={640} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Post return and credit</PrimaryButton></>}>
      {issue.lines.map((l, i) => {
        const can = l.quantity - l.returned_qty;
        return (
          <div key={i} className="d-flex align-items-center gap-2 mb-2">
            <div style={{ flex: 1, fontSize: 14 }}>
              {l.sku} <span style={hint}>· issued {l.quantity}, returned {l.returned_qty}</span>
            </div>
            <input type="number" min={0} max={can} step="any" disabled={can <= 0} className="form-control" style={{ ...inputStyle, width: 110 }} aria-label={`Return quantity for ${l.sku}`} value={qty[i] ?? ""} onChange={(e) => setQty({ ...qty, [i]: e.target.value })} />
          </div>
        );
      })}
      <Field label="Reason">
        <input className="form-control" style={inputStyle} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div style={hint}>The parts go back into the issuing warehouse; a credit memo is raised against the invoice.</div>
    </Panel>
  );
}

function OrderView({ id, onHide, onChanged }) {
  const { data: so, loading, error, reload } = useLoad(() => dmsService.getOrder(id), [id]);
  const cancelReasons = useLookup("CANCEL_REASON");
  const [cancelling, setCancelling] = useState(false);
  const [returning, setReturning] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  const st = so ? so.status : null;
  const canIssue = so && ["BOOKED", "PARTIALLY_DELIVERED"].includes(st) && so.lines.some((l) => l.part_state && l.part_state.reserved > 0);
  return (
    <Panel title={so ? `Parts order ${so.so_number}` : "Parts order"} onHide={onHide}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {so ? (
          <>
            <DocHead title="" number={so.customer_name} status={st} sub={`${so.location_name || ""} · warehouse ${so.parts ? so.parts.warehouse_code : ""} · ${formatDate(so.order_date)}${so.customer_po ? ` · PO ${so.customer_po}` : ""}${so.quote_number ? ` · from ${so.quote_number}` : ""}`}>
              {st === "ENTERED" ? <PrimaryButton onClick={() => run(() => dmsService.orderAction(id, "book"), refresh)}>Book</PrimaryButton> : null}
              {canIssue ? <PrimaryButton onClick={() => run(() => dmsService.issueParts(id), refresh)}>Issue reserved parts</PrimaryButton> : null}
              {["ENTERED", "BOOKED"].includes(st) ? <SecondaryButton onClick={() => setCancelling(true)}>Cancel order</SecondaryButton> : null}
              <SecondaryButton onClick={() => dmsService.orderPdf(id, `${so.so_number}.pdf`)}>PDF</SecondaryButton>
            </DocHead>
            <Section title="Parts">
              <DataTable
                rows={so.lines}
                rowKey={(l) => l.line_no}
                columns={[
                  { label: "#", render: (l) => l.line_no },
                  { label: "Part", render: (l) => l.description },
                  { label: "Ordered", num: true, render: (l) => Number(l.quantity) },
                  { label: "Issued", num: true, render: (l) => (l.part_state ? l.part_state.issued : 0) },
                  { label: "Reserved", num: true, render: (l) => (l.part_state ? l.part_state.reserved : 0) },
                  { label: "Back order", num: true, render: (l) => (l.part_state && l.part_state.back_ordered ? <b style={{ color: "#92400E" }}>{l.part_state.back_ordered}</b> : 0) },
                  { label: "Net", num: true, render: (l) => fmtMoney(l.net_amount) },
                  { label: "Status", render: (l) => <DmsBadge status={l.status} /> },
                  {
                    label: "",
                    render: (l) =>
                      ["ENTERED", "BOOKED"].includes(st) && l.status === "OPEN" && !(l.part_state && l.part_state.issued) && so.lines.filter((x) => x.status !== "CANCELLED").length > 1 ? (
                        <button type="button" style={linkBtn} onClick={() => window.confirm(`Cancel line ${l.line_no}?`) && run(() => dmsService.lineAction(id, l.id, "cancel"), refresh)}>
                          Cancel line
                        </button>
                      ) : null,
                  },
                ]}
              />
              <Totals goods="Parts" doc={so} extra={Number(so.advance_received) ? [["Advance received", fmtMoney(so.advance_received)]] : []} />
            </Section>
            <Section title="Issues and invoices">
              {so.parts && so.parts.issues.length ? (
                so.parts.issues.map((iss) => (
                  <div key={iss.issue_number} className="mb-3">
                    <div className="d-flex flex-wrap justify-content-between gap-2" style={{ fontSize: 14 }}>
                      <b>
                        {iss.issue_number} · {formatDate(iss.issue_date)} {iss.trx_number ? `· invoice ${iss.trx_number}` : ""}
                      </b>
                      <span className="d-flex gap-3">
                        {iss.status === "POSTED" ? (
                          <button type="button" style={linkBtn} onClick={() => run(() => dmsService.invoicePartsIssue(iss.id), refresh)}>
                            Create invoice
                          </button>
                        ) : (
                          <>
                            <button type="button" style={linkBtn} onClick={() => finService.transactionPdf(iss.ar_trx_id, `${iss.trx_number}.pdf`)}>
                              Tax invoice
                            </button>
                            <button type="button" style={linkBtn} onClick={() => setReturning(iss)}>
                              Return parts
                            </button>
                          </>
                        )}
                      </span>
                    </div>
                    {iss.lines.map((l, i) => (
                      <div key={i} style={{ fontSize: 13 }}>
                        {l.sku} × {l.quantity}
                        {l.returned_qty ? ` (returned ${l.returned_qty})` : ""}
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div style={hint}>{st === "ENTERED" ? "Book the order to reserve the stock." : "Nothing issued yet."}</div>
              )}
              {so.parts && so.parts.returns.length ? (
                <div className="mt-2">
                  {so.parts.returns.map((r) => (
                    <div key={r.return_number} style={{ fontSize: 13, color: "#4B5563" }}>
                      Return {r.return_number} · {formatDate(r.return_date)} · credit {r.trx_number} {fmtMoney(r.credit_amount)}
                    </div>
                  ))}
                </div>
              ) : null}
            </Section>
            <Section title="Action history">
              <ActionHistory actions={so.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      {returning ? <ReturnDrawer issue={returning} onHide={() => setReturning(null)} onDone={refresh} /> : null}
      <FormDrawer
        show={cancelling}
        title="Cancel the order"
        onHide={() => setCancelling(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.orderAction(id, "cancel", clean)}
        fields={[
          { key: "reason", label: "Reason", type: "select", required: true, options: cancelReasons },
          { key: "remarks", label: "Remarks", max: 500 },
        ]}
      />
    </Panel>
  );
}

function DocList({ kind, reloadKey, onOpen }) {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => (kind === "quote" ? dmsService.listQuotes({ category: "PARTS", status: status || undefined, q: q || undefined }) : dmsService.listOrders({ category: "PARTS", status: status || undefined, q: q || undefined })), [kind, status, q, reloadKey]);
  const statuses = kind === "quote" ? ["DRAFT", "PENDING_APPROVAL", "APPROVED", "PENDING_WITH_CUSTOMER", "WON", "LOST", "EXPIRED", "CANCELLED"] : ["ENTERED", "PENDING_APPROVAL", "BOOKED", "PARTIALLY_DELIVERED", "DELIVERED", "CLOSED", "CANCELLED"];
  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="Number, customer, phone" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ width: 220 }}>
          <AppDropdown id={`pd-st-${kind}`} value={status} options={[{ label: "All", value: "" }, ...statuses.map((s) => ({ label: s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, " "), value: s }))]} onChange={(e) => setStatus(e.value)} />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => onOpen(r.id)}
          empty={<EmptyState title={kind === "quote" ? "No parts quotations" : "No parts orders"} />}
          columns={[
            { label: kind === "quote" ? "Quotation" : "Order", render: (r) => <b>{kind === "quote" ? `${r.quote_number}${r.revision ? ` rev ${r.revision}` : ""}` : r.so_number}</b> },
            { label: "Date", render: (r) => formatDate(kind === "quote" ? r.quote_date : r.order_date) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Advisor", render: (r) => r.salesperson_name },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
    </>
  );
}

const TABS = [
  { key: "orders", label: "Orders" },
  { key: "quotes", label: "Quotations" },
  { key: "stock", label: "Part availability" },
  { key: "back-orders", label: "Back orders" },
];

export default function PartsSales() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "orders";
  const [creating, setCreating] = useState(null);
  const [openQuote, setOpenQuote] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const bump = () => setReloadKey((k) => k + 1);
  return (
    <div style={pageStyle}>
      <PageHeader
        title="Parts Sales"
        subtitle="Parts quotations and orders, availability, back orders (stock, warehouses and transfers are in Inventory)"
        actions={
          <>
            <SecondaryButton onClick={() => setCreating("quote")}>New quotation</SecondaryButton>
            <PrimaryButton onClick={() => setCreating("order")}>New order</PrimaryButton>
          </>
        }
      />
      <Tabs tabs={TABS} active={tab} onChange={(k) => setParams({ tab: k })} />
      {tab === "orders" ? <DocList kind="order" reloadKey={reloadKey} onOpen={setOpenOrder} /> : null}
      {tab === "quotes" ? <DocList kind="quote" reloadKey={reloadKey} onOpen={setOpenQuote} /> : null}
      {tab === "stock" ? <PartsStock /> : null}
      {tab === "back-orders" ? <BackOrders onOpenOrder={setOpenOrder} /> : null}
      {creating ? (
        <NewDoc
          kind={creating}
          onHide={() => setCreating(null)}
          onDone={(id) => {
            bump();
            if (creating === "quote") setOpenQuote(id);
            else setOpenOrder(id);
          }}
        />
      ) : null}
      {openQuote ? (
        <QuoteView
          id={openQuote}
          onHide={() => setOpenQuote(null)}
          onChanged={bump}
          onOrder={(soId) => {
            setOpenQuote(null);
            setParams({ tab: "orders" });
            setOpenOrder(soId);
          }}
        />
      ) : null}
      {openOrder ? <OrderView id={openOrder} onHide={() => setOpenOrder(null)} onChanged={bump} /> : null}
    </div>
  );
}
